/* eslint-disable max-lines */
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { FC, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useFetch from "../../hooks/useFetch";
import { IWalletSummary } from "../../interfaces/wallet/Wallet.types";
import { searchWallets } from "../../services/Wallet.service";
import { eur } from "../../utils/eur";
import Table, { Row } from "../ui/table";
import Pagination from "../ui/table/Pagination";
import Can from "../users/permissions/Can";
import IssueCreditsModal from "./IssueCreditsModal";

const columns: (string | number)[] = [
  "Email",
  "Gjithsej",
  "Për bileta",
  "Për eSIM",
  "Përdor deri",
  "Aktiviteti i fundit",
  ""
];

const PAGE_SIZE = 25;

const Wallets: FC = () => {
  // Two email states on purpose: `email` is what is being typed, `query` is what the list is showing.
  // Paging must not silently re-filter to a half-typed address.
  const [email, setEmail] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const { response, loading, error, handleFunc } = useFetch(searchWallets);

  const load = useCallback(() => {
    void handleFunc({
      email: query || undefined,
      pageNumber: page,
      pageSize: PAGE_SIZE
    });
  }, [handleFunc, query, page]);

  useEffect(() => {
    load();
  }, [load]);

  const search = () => {
    // A new term always starts at page one — page 4 of the old result set is almost never a page of the
    // new one, and the server would answer with an empty table.
    setPage(1);
    setQuery(email.trim());
  };

  const wallets: IWalletSummary[] = response?.data?.result ?? [];

  return (
    <div className="px-5 pb-8">
      <div className="flex items-end justify-between gap-4 mb-4">
        <div className="w-full max-w-md">
          <label
            htmlFor="wallet-search"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Kërko sipas email-it
          </label>
          <div className="flex gap-2">
            <input
              id="wallet-search"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="email@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            <button
              type="button"
              onClick={search}
              aria-label="Kërko"
              className="px-3 py-2 border border-gray-300 rounded-lg"
            >
              <MagnifyingGlassIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <Can permissions="wallet.credit.issue">
          <IssueCreditsModal onSuccess={load} />
        </Can>
      </div>

      {/* Same failed-state pattern as CreditReports: say it failed and offer a retry, instead of
          an empty table that reads as "no wallets". */}
      {!!error && !loading && (
        <p className="text-sm text-red mb-2">
          Lista nuk u ngarkua dot.{" "}
          <button type="button" className="underline" onClick={load}>
            Provo përsëri
          </button>
        </p>
      )}

      <Table titles={columns} loading={loading}>
        {wallets.map((w) => (
          <Row
            key={w.walletId}
            email={
              <Link className="text-primary" to={`${w.walletId}`}>
                {w.email}
              </Link>
            }
            total={eur(w.total)}
            travel={eur(w.spendableOnTravel)}
            esim={eur(w.spendableOnEsim)}
            nextExpiry={
              w.nextExpiryAtUtc
                ? dayjs(w.nextExpiryAtUtc).format("DD/MM/YYYY")
                : "-"
            }
            lastActivity={
              w.lastTransactionAtUtc
                ? dayjs(w.lastTransactionAtUtc).format("DD/MM/YYYY")
                : "-"
            }
            detail={
              <Link className="text-primary text-sm" to={`${w.walletId}`}>
                Detajet
              </Link>
            }
          />
        ))}
      </Table>

      <Pagination
        onPageChange={({ selected }) => setPage(selected + 1)}
        totalCount={response?.data?.totalCount ?? 0}
        page={page}
        length={wallets.length}
        pageSize={response?.data?.pageSize ?? PAGE_SIZE}
        viewingOf={false}
        className="border-none px-0 py-0"
      />
    </div>
  );
};

export default Wallets;
